import type {
  RaidPlayerId,
  RaidStage,
  RaidStageBuildSnapshot,
  RaidStageDiff,
  RaidStageId,
  RaidStageKind,
} from '../types'

export interface BuildStageSelection {
  readonly stageId: RaidStageId
  readonly stageKind: RaidStageKind
  readonly stageName: string
  readonly weaponSlugs: ReadonlyArray<string>
}

export function activeStageBuild(
  snapshots: ReadonlyArray<RaidStageBuildSnapshot>,
  activeIndex: number,
): RaidStageBuildSnapshot | null {
  return snapshots.find((snapshot) => snapshot.orderIndex === activeIndex) ?? null
}

function exactWeaponPair(weapons: ReadonlyArray<string>): ReadonlyArray<string> | null {
  const first = weapons[0]
  const second = weapons[1]
  if (weapons.length !== 2 || first === undefined || second === undefined) return null
  if (first.trim() === '' || second.trim() === '' || first === second) return null
  return [first, second]
}

/**
 * Resolve the effective named-stage loadout for one raid snapshot player.
 * Sparse stage rows inherit their active pair from canonical ALL, while an
 * explicit build override uses its own `after` snapshot.
 */
export function buildStageSelectionsForPlayer(
  stages: ReadonlyArray<RaidStage>,
  diffs: ReadonlyArray<RaidStageDiff>,
  playerId: RaidPlayerId,
  currentWeapons: ReadonlyArray<string>,
): ReadonlyArray<BuildStageSelection> {
  const playerDiffs = diffs.filter((diff) => diff.playerId === playerId)
  const baselineDiff = playerDiffs.find((diff) => diff.build !== null)?.build ?? null
  const baselineSnapshot = baselineDiff === null
    ? null
    : activeStageBuild(baselineDiff.before, baselineDiff.beforeActiveBuildIndex)
  const baselineWeapons = exactWeaponPair(baselineSnapshot?.weaponSlugs ?? currentWeapons)

  return stages.flatMap((stage) => {
    const stageBuild = playerDiffs.find((diff) => diff.stageId === stage.id)?.build ?? null
    const stageSnapshot = stageBuild === null
      ? null
      : activeStageBuild(stageBuild.after, stageBuild.afterActiveBuildIndex)
    const weaponSlugs = exactWeaponPair(stageSnapshot?.weaponSlugs ?? baselineWeapons ?? [])
    return weaponSlugs === null
      ? []
      : [{
          stageId: stage.id,
          stageKind: stage.kind,
          stageName: stage.name,
          weaponSlugs,
        }]
  })
}
